import { useCallback, useMemo, useState } from 'react';

import { UPLOADING_STATUS, type UploadingStatus } from 'src/data/common';
import {
  type CustomerResource,
  getCreatedResourceMessage,
  hasResourcesWithUploadId,
  type ResourceContentType,
} from 'src/data/performance';
import { PROMPT_ACTION, type PromptAction } from 'src/data/prompt';
import type { ID } from 'src/lib/collection';
// @ts-expect-error - useNotificationActions doesn't have TypeScript definitions
import { useNotificationActions } from 'src/store/notification';

/**
 * Data for inserting a resource, with uploadId to replace the upload placeholder.
 * @property resource - The customer resource to insert.
 * @property uploadId - Temporary ID to locate and replace the upload placeholder.
 */
interface ResourceInsertionData {
  resource: CustomerResource;
  uploadId: ID;
}

/**
 * Union type for prompt action data, where each action expects a specific type:
 * - Insertion action: uses {@link ResourceInsertionData}
 * - Removal action: uses {@link CustomerResource}
 */
type PromptActionData = ResourceInsertionData | CustomerResource;

/**
 * Resource type used while file upload is in progress.
 * At this stage, the resource is not yet a full CustomerResource.
 * @property file - The native File object being uploaded.
 * @property contentType - The content type of the resource.
 */
interface InProgressResource extends Pick<CustomerResource, 'contentType'> {
  file: File;
}

interface UploadProgressData {
  status: UploadingStatus;
  resource: CustomerResource | InProgressResource;
  error: string;
  uploadId: ID;
}

/**
 * Placeholder resource shown while upload is in progress.
 * @property id - Temporary upload ID used to track the upload.
 * @property name - File name from the uploading resource.
 * @property contentType - Content type of the uploading resource.
 */
interface UploadingResource {
  id: ID;
  name?: string;
  contentType?: ResourceContentType;
}

/** Resource that can be either a fully uploaded CustomerResource or an uploading placeholder. */
type AttachedResource = CustomerResource | UploadingResource;

export const usePromptFilesSupport = () => {
  const notifications = useNotificationActions();

  const [attachedResources, setAttachedResources] = useState<AttachedResource[]>([]);

  const hasUploadingResources = useMemo(
    () => hasResourcesWithUploadId(attachedResources as CustomerResource[]),
    [attachedResources]
  );

  const handlePromptAction = useCallback((action: PromptAction, data: PromptActionData) => {
    switch (action) {
      case PROMPT_ACTION.insertFileResource: {
        const { resource, uploadId } = data as ResourceInsertionData;

        setAttachedResources((prev) => {
          // Replace "uploading" placeholder with the uploaded resource
          if (uploadId) {
            return prev.map((r) => (r.id === uploadId ? resource : r));
          }

          // Add resource to the list if it doesn't exist
          if (!prev.find((r) => r.id === resource.id)) {
            return [...prev, resource];
          }
          return prev;
        });
        break;
      }

      case PROMPT_ACTION.removeFileResource: {
        const { id: resourceId } = data as CustomerResource;

        setAttachedResources((prev) => prev.filter((image) => image.id !== resourceId));
        break;
      }

      default:
        break;
    }
  }, []);

  const handleUploadProgress = useCallback(
    ({ status, resource, error, uploadId }: UploadProgressData) => {
      switch (status) {
        case UPLOADING_STATUS.started: {
          const actualizedResource = resource as InProgressResource;
          const fileName = actualizedResource?.file?.name;

          setAttachedResources((prev) => [
            ...prev,
            { id: uploadId, name: fileName, contentType: resource?.contentType },
          ]);
          break;
        }

        case UPLOADING_STATUS.success: {
          notifications.displaySuccess(getCreatedResourceMessage(resource as CustomerResource));
          break;
        }

        case UPLOADING_STATUS.error: {
          setAttachedResources((prev) => prev.filter((item) => item.id !== uploadId));
          notifications.displayError(error);
          break;
        }
      }
    },
    [notifications]
  );

  return {
    attachedResources,
    hasUploadingResources,
    handlePromptAction,
    handleUploadProgress,
  };
};
